import { roundArea } from "../../area"
import { CropYear } from "../../cropYear"
import { ICrop } from "../../crops"
import { getFieldCroppingArea, ICropping } from "../../fields"
import { sum } from "../../utils/array"
import { isDate } from "../../utils/utils"
import { getConcurrentCrops } from "../history"
import { checkDatesAndCroppingYear, checkNonPerennialDestructionDate, checkPerennialCroppingYear } from "./dates"

const errorKeys = [
    "date",
    "year",
    "twoyears",
    "PerennialCroppingYear",
    "AfterNextYear",
    "Concurrent"
] as const

type ErrorKey = typeof errorKeys[number]

const datePropertyKeys = [
    "SowingDate",
    "HarvestDate",
    "DestructionDate"
]

type DatePropertyKey = typeof datePropertyKeys[number]

const propertiesAndErrors = datePropertyKeys.flatMap(property => errorKeys.map(error => [property, error])) as [DatePropertyKey, ErrorKey][]

export class DateValidationService {

    public dateErrors = {};

    private startDate: Date;
    private endDate: Date;

    private firstCroppingYear: number;
    private lastCroppingYear: number;

    constructor(
        private cropping: ICropping,
        private crop: ICrop,
        private useDestructionDate: boolean,
        private formProvider: () => ng.IFormController,
        private areaProvider: () => number,
        private cropYear: CropYear,
        croppingYears: number[]) {

        this.firstCroppingYear = Math.min.apply(Math, croppingYears);
        this.lastCroppingYear = Math.max.apply(Math, croppingYears);

    }

    public validateDates(startChanged?: boolean) {
        this.validateDatesWithDate(this.cropping.SowingDate, (this.useDestructionDate ? this.cropping.DestructionDate : this.cropping.HarvestDate), startChanged);
    }

    public validateDatesWithDate(start: Date, end: Date, startChanged?: boolean): boolean {

        this.startDate = start
        this.endDate = end

        this.dateErrors = {}
        this.resetForm()

        this.checkStartBeforeEnd(startChanged)

        if (this.crop && this.crop.Perennial) {
            this.checkPerennialCroppingYear(start, true)
            this.checkPerennialCroppingYear(end, false)
        }
        else {
            this.checkCroppingYear()
            this.checkNonPerennialDestructionDate(end)
        }

        this.checkConcurrentCrops(start, end)

        if (Object.keys(this.dateErrors).length == 0) {
            this.dateErrors = null;
            return true
        }

        return false

    }

    private checkStartBeforeEnd(startChanged: boolean) {

        var start = this.startDate;
        var end = this.endDate;

        var valid = !(isDate(start) && isDate(end) && (start >= end));

        this.setDateValidity(true, "date", valid);
        this.setDateValidity(false, "date", valid);

        const attr: string = (startChanged ? "SowingDate" : this.useDestructionDate ? "DestructionDate" : "HarvestDate");

        if (!valid) {
            this.dateErrors[attr + "/date"] = true;
        }

    }

    private resetForm() {

        const form = this.formProvider()

        if (form) {

            for (const [p, e] of propertiesAndErrors) {
                form[p]?.$setValidity(e, true)
            }

        }


    }
    private setDateValidity(start: boolean, key: ErrorKey, valid: boolean) {

        const form = this.formProvider();

        if (form) {

            const name: string = (start ? "SowingDate" : this.useDestructionDate ? "DestructionDate" : "HarvestDate");

            const mdl = form[name] as ng.INgModelController
            if (mdl) mdl.$setValidity(key, valid);

        }
    }

    private checkCroppingYear() {

        const { startDate, endDate, cropYear } = this

        let ok = true
        const { invalidYear, twoYears } = checkDatesAndCroppingYear(startDate, endDate, cropYear)

        if (invalidYear) {
            this.dateErrors["year"] = true
            ok = false
        }

        if (twoYears) {
            this.dateErrors["twoyears"] = true
            ok = false
        }

        this.setDateValidity(true, "year", ok)
        this.setDateValidity(false, "year", ok)

    }

    private checkPerennialCroppingYear(date: Date, start: boolean) {

        if (!isDate(date)) return

        const valid = checkPerennialCroppingYear(date, start, this.firstCroppingYear, this.lastCroppingYear)

        if (!valid) {
            this.dateErrors[(start ? "Start" : "End") + "/PerennialCroppingYear"] = true
        }

        this.setDateValidity(start, "PerennialCroppingYear", valid)

    }


    private checkNonPerennialDestructionDate(end: Date) {

        if (!isDate(end)) return

        const valid = checkNonPerennialDestructionDate(end, this.useDestructionDate, this.cropYear)

        if (!valid) {
            this.dateErrors["DestructionDate/AfterNextYear"] = true
        }

        this.setDateValidity(false, "AfterNextYear", valid)

    }


    private checkConcurrentCrops(start: Date, end: Date) {

        const areaTolerance = 0.1

        const check = () => {

            const field = this.cropping.Field
            if (!field) return true

            const cropYear = this.cropYear
            if (!cropYear) return true

            const fieldArea = getFieldCroppingArea(field)
            if (!fieldArea) return true

            const area = this.areaProvider()
            if (!area) return true

            if (this.crop && this.crop.Perennial) {
                start = start >= cropYear.StartDate ? start : cropYear.StartDate
                end = isDate(end) && (end <= cropYear.EndDate) ? end : cropYear.EndDate
            }

            if (!isDate(start)) return true
            if (!isDate(end)) return true

            let cc = getConcurrentCrops(this.cropping, ({ dates: { start, end } }))
            // 210210 - Usersnap 1968 - Ignore uncropped areas when validating dates/areas
            cc = cc.filter(c => !!c.CropId)
            if (!cc.length) return true

            const ccarea = roundArea(area + sum(cc, c => c.Area))

            if (fieldArea >= (ccarea - areaTolerance)) {
                return true
            }

            const msg = `There are other crops at the same time on this field, and the overall cropped area (${ccarea} ha) exceeds that of the field (${fieldArea} ha). Please check the dates and area.`

            return msg

        }

        const msg = check()
        const ok = msg === true
        const key = "Concurrent"

        this.setDateValidity(true, key, ok)
        this.setDateValidity(false, key, ok)

        if (!ok) {
            this.dateErrors[key] = msg
        }

    }

}
